/**
 * Mini-Notation Parser for Tidal Cycles / Strudel patterns
 * Supports: sequences, nesting, multiplication, division, angle brackets,
 * rests, polyphony, elongation, replication, randomness, Euclidean rhythms,
 * hold/sustain (;N where N is duration in seconds)
 */

export interface PatternEvent {
  type: 'note' | 'rest' | 'chord'
  values: string[] // For chords, multiple values; for notes, single value
  duration: number // Relative duration (1.0 = normal beat)
  velocity: number // Velocity (0-1, default 1)
  weight: number // For @ elongation
  replicate: number // For ! replication
  probability: number // For ? randomness (0-1)
  multiplier: number // For * multiplication (element-level)
  divisor: number // For / division (element-level)
  glide: number // For \ glide (0 = no glide, >0 = glide with exponent)
  hold: number // For ; hold/sustain duration in seconds (0 = no hold)
  line: number // Line number (1-based) where event starts
  column: number // Column number (1-based) where event starts
  length: number // Length of the event in characters
}

export interface PatternSequence {
  events: PatternEvent[]
  multiplier: number // For * multiplication
  divisor: number // For / division
  type: 'sequential' | 'alternating' | 'random' // For <>, |, or normal
}

export interface EuclideanRhythm {
  beats: number
  segments: number
  offset: number
}

/**
 * Calculate Greatest Common Divisor (GCD) of two numbers
 */
function gcd(a: number, b: number): number {
  a = Math.abs(a)
  b = Math.abs(b)
  while (b !== 0) {
    const temp = b
    b = a % b
    a = temp
  }
  return a
}

/**
 * Calculate Least Common Multiple (LCM) of two numbers
 */
function lcm(a: number, b: number): number {
  return Math.abs(a * b) / gcd(a, b)
}

export class PatternParser {
  private pos = 0
  private input = ''
  private originalInput = ''
  private line = 1
  private column = 1
  private lineOffset = 0
  private columnOffset = 0

  parse(pattern: string, lineOffset: number = 0, columnOffset: number = 0, originalPattern?: string): PatternSequence {
    this.originalInput = originalPattern || pattern
    this.input = pattern.trim()
    this.pos = 0
    this.lineOffset = lineOffset
    this.columnOffset = columnOffset

    // Calculate where the trimmed content starts in the original pattern
    const original = originalPattern || pattern
    const trimmedStart = this.findTrimmedStart(original)
    const trimmedStartPos = this.positionToLineColumn(original, trimmedStart)

    // lineOffset is the line where the string token starts (where quote is)
    // columnOffset is the column where content starts (after quote)
    // Add the position of trimmed content within the original string
    this.line = lineOffset + (trimmedStartPos.line - 1)
    this.column = columnOffset + (trimmedStartPos.column - 1)

    return this.parseSequence()
  }

  private findTrimmedStart(str: string): number {
    let pos = 0
    while (pos < str.length && (str[pos] === ' ' || str[pos] === '\t' || str[pos] === '\n' || str[pos] === '\r')) {
      pos++
    }
    return pos
  }

  private positionToLineColumn(str: string, position: number): { line: number; column: number } {
    if (position === 0) return { line: 1, column: 1 }
    const before = str.substring(0, position)
    const line = before.split('\n').length
    const lastNewline = before.lastIndexOf('\n')
    const column = lastNewline === -1 ? before.length + 1 : position - lastNewline
    return { line, column }
  }

  private parseSequence(): PatternSequence {
    const events: PatternEvent[] = []
    let multiplier = 1
    let divisor = 1
    let type: 'sequential' | 'alternating' | 'random' = 'sequential'

    this.skipWhitespace()

    // Check for angle brackets (alternating)
    if (this.peek() === '<') {
      type = 'alternating'
      this.advancePosition()
      this.skipWhitespace()
    }

    // Parse events until we hit a closing bracket or end
    let lastPos = -1
    while (this.pos < this.input.length) {
      // Guard against infinite loop
      if (lastPos === this.pos) {
        // Position didn't advance, skip character and continue
        this.advancePosition()
        continue
      }
      lastPos = this.pos

      const char = this.peek()

      // For alternating patterns, check for closing bracket before modifiers
      if (type === 'alternating' && char === '>') {
        break
      }

      // Check for sequence-level modifiers (multiplication/division)
      // Only break on these if we're not in an alternating pattern waiting for '>'
      if (char === '*' || char === '/') {
        break
      }

      if (char === '>' || char === ']' || char === ')') {
        break
      }

      if (char === ',') {
        // Skip standalone commas (they're handled in chord parsing)
        this.advancePosition()
        this.skipWhitespace()
        continue
      }

      if (char === '|') {
        // Random choice separator
        type = 'random'
        this.advancePosition()
        this.skipWhitespace()
        continue
      }

      const event = this.parseEvent()
      if (event) {
        events.push(event)
      }

      this.skipWhitespace()
    }

    // Check for closing angle bracket
    if (type === 'alternating' && this.peek() === '>') {
      this.advancePosition()
    }

    // Check for multiplication or division
    this.skipWhitespace()
    if (this.peek() === '*') {
      this.advancePosition()
      multiplier = this.parseNumber()
    }
    else if (this.peek() === '/') {
      this.advancePosition()
      divisor = this.parseNumber()
    }

    const result = {
      events,
      multiplier,
      divisor,
      type,
    }
    return result
  }

  private parseEvent(): PatternEvent | null {
    this.skipWhitespace()

    const char = this.peek()

    // End of input or closing delimiter
    if (!char || char === '>' || char === ']' || char === ')') {
      return null
    }

    // Capture start position
    const startPos = this.pos
    const startLine = this.line
    const startColumn = this.column

    // Rest
    if (char === '~' || char === '_') {
      this.advancePosition()
      const event = this.applyModifiers({
        type: 'rest',
        values: [],
        duration: 1,
        velocity: 0,
        weight: 1,
        replicate: 1,
        probability: 1,
        multiplier: 1,
        divisor: 1,
        glide: 0,
        hold: 0,
        line: startLine,
        column: startColumn,
        length: 0, // Will be set after modifiers
      })
      // Calculate length excluding trailing whitespace and newlines
      const endPos = this.pos
      let actualEndPos = endPos
      while (actualEndPos > startPos) {
        const char = this.input[actualEndPos - 1]
        if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
          actualEndPos--
        }
        else {
          break
        }
      }
      event.length = actualEndPos - startPos
      return event
    }

    // Standalone velocity modifier (e.g., .3, .5)
    if (char === '.' && this.pos + 1 < this.input.length) {
      const nextChar = this.input[this.pos + 1]
      if (nextChar >= '0' && nextChar <= '9') {
        const event = this.applyModifiers({
          type: 'note',
          values: [],
          duration: 1,
          velocity: 1,
          weight: 1,
          replicate: 1,
          probability: 1,
          multiplier: 1,
          divisor: 1,
          glide: 0,
          hold: 0,
          line: startLine,
          column: startColumn,
          length: 0, // Will be set after modifiers
        })
        // Calculate length excluding trailing whitespace
        const endPos = this.pos
        let actualEndPos = endPos
        while (actualEndPos > startPos
          && (this.input[actualEndPos - 1] === ' ' || this.input[actualEndPos - 1] === '\t'))
        {
          actualEndPos--
        }
        event.length = actualEndPos - startPos
        return event
      }
    }

    // Check for square brackets - could be chord or nested sequence
    if (char === '[') {
      // Look ahead to determine if it's a chord (has commas or |) or subdivision (has spaces)
      const lookaheadStart = this.pos + 1
      let lookaheadPos = lookaheadStart
      let hasComma = false
      let hasPipe = false
      let hasSpace = false
      let bracketDepth = 1

      // Scan until closing bracket
      while (lookaheadPos < this.input.length && bracketDepth > 0) {
        const c = this.input[lookaheadPos]
        if (c === '[') bracketDepth++
        if (c === ']') bracketDepth--
        if (bracketDepth === 1) {
          if (c === ',') hasComma = true
          if (c === '|') hasPipe = true
          if (c === ' ') hasSpace = true
        }
        lookaheadPos++
      }

      // If has commas or pipes and no spaces, it's a chord
      const isChord = (hasComma || hasPipe) && !hasSpace

      if (isChord) {
        // Parse as chord: skip '[', parse comma or pipe-separated values, skip ']'
        this.advancePosition() // Skip '['
        const values: string[] = []

        // Parse first value
        const firstValue = this.parseValue()
        if (firstValue) values.push(firstValue)

        // Parse remaining comma or pipe-separated values
        this.skipWhitespace()
        let chordLoopCount = 0
        const maxChordValues = 20

        while ((this.peek() === ',' || this.peek() === '|') && chordLoopCount < maxChordValues) {
          chordLoopCount++
          this.advancePosition() // Skip comma or pipe
          this.skipWhitespace()
          const nextValue = this.parseValue()
          if (nextValue) values.push(nextValue)
          this.skipWhitespace()
        }

        // Skip closing ']'
        if (this.peek() === ']') {
          this.advancePosition()
        }

        // If pipe was used, mark this as a random choice chord
        const event: PatternEvent = {
          type: 'chord',
          values,
          duration: 1,
          velocity: 1,
          weight: 1,
          replicate: 1,
          probability: 1,
          multiplier: 1,
          divisor: 1,
          glide: 0,
          hold: 0,
          line: startLine,
          column: startColumn,
          length: 0, // Will be set after modifiers
        }
        if (hasPipe) {
          ;(event as any).randomChoice = true
        }

        const modifiedEvent = this.applyModifiers(event)
        // Calculate length excluding trailing whitespace and newlines
        const endPos = this.pos
        let actualEndPos = endPos
        while (actualEndPos > startPos) {
          const char = this.input[actualEndPos - 1]
          if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
            actualEndPos--
          }
          else {
            break
          }
        }
        modifiedEvent.length = actualEndPos - startPos
        return modifiedEvent
      }
      else {
        // Parse as nested sequence (subdivision)
        this.advancePosition() // Skip '['
        const nested = this.parseSequence()
        if (this.peek() === ']') {
          this.advancePosition() // Skip ']'
        }

        // After closing bracket, check for sequence-level modifiers (no whitespace allowed)
        if (this.peek() === '*') {
          this.advancePosition()
          nested.multiplier = this.parseNumber()
          this.skipWhitespace()
        }
        else if (this.peek() === '/') {
          this.advancePosition()
          nested.divisor = this.parseNumber()
          this.skipWhitespace()
        }

        // Convert nested sequence to event(s)
        const event: PatternEvent = {
          type: 'note',
          values: ['__nested__'], // Placeholder
          duration: 1,
          velocity: 1,
          weight: 1,
          replicate: 1,
          probability: 1,
          multiplier: 1,
          divisor: 1,
          glide: 0,
          hold: 0,
          line: startLine,
          column: startColumn,
          length: 0, // Will be set after modifiers
        } // Store nested data for later processing
        ;(event as any).nested = nested

        // Apply element-level modifiers (like @, !, ?) to the nested event
        // But only if they're immediately adjacent (no whitespace)
        const modifiedEvent = this.applyModifiersNoSkipWhitespace(event)
        // Calculate length excluding trailing whitespace and newlines
        const endPos = this.pos
        let actualEndPos = endPos
        while (actualEndPos > startPos) {
          const char = this.input[actualEndPos - 1]
          if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
            actualEndPos--
          }
          else {
            break
          }
        }
        modifiedEvent.length = actualEndPos - startPos
        return modifiedEvent
      }
    }

    // Check for nested angle brackets (alternating pattern)
    if (char === '<') {
      // Parse as nested alternating sequence
      // Don't skip '<' - let parseSequence() detect it and set type to 'alternating'
      const nested = this.parseSequence()
      // parseSequence() will consume both '<' and '>'

      // After closing bracket, check for sequence-level modifiers (no whitespace allowed)
      if (this.peek() === '*') {
        this.advancePosition()
        nested.multiplier = this.parseNumber()
        this.skipWhitespace()
      }
      else if (this.peek() === '/') {
        this.advancePosition()
        nested.divisor = this.parseNumber()
        this.skipWhitespace()
      }

      // Convert nested sequence to event(s)
      const event: PatternEvent = {
        type: 'note',
        values: ['__nested__'], // Placeholder
        duration: 1,
        velocity: 1,
        weight: 1,
        replicate: 1,
        probability: 1,
        multiplier: 1,
        divisor: 1,
        glide: 0,
        line: startLine,
        column: startColumn,
        length: 0, // Will be set after modifiers
      } // Store nested data for later processing
      ;(event as any).nested = nested

      // Apply element-level modifiers (like @, !, ?) to the nested event
      // But only if they're immediately adjacent (no whitespace)
      const modifiedEvent = this.applyModifiersNoSkipWhitespace(event)
      // Calculate length excluding trailing whitespace and newlines
      const endPos = this.pos
      let actualEndPos = endPos
      while (actualEndPos > startPos) {
        const char = this.input[actualEndPos - 1]
        if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
          actualEndPos--
        }
        else {
          break
        }
      }
      modifiedEvent.length = actualEndPos - startPos
      return modifiedEvent
    }

    // Parse value(s) - could be a single note or chord
    const values: string[] = []

    // First value
    const value = this.parseValue()
    if (!value) return null
    values.push(value)

    // Check for chord (comma-separated values)
    this.skipWhitespace()
    let isChord = false
    let chordLoopCount = 0
    const maxChordValues = 20 // Safety limit

    while (this.peek() === ',' && chordLoopCount < maxChordValues) {
      chordLoopCount++
      const beforeComma = this.pos
      this.advancePosition() // Skip comma
      this.skipWhitespace()

      const nextValue = this.parseValue()
      if (nextValue) {
        values.push(nextValue)
        isChord = true
      }
      else {
        // No value after comma, restore position and break
        this.pos = beforeComma
        break
      }

      this.skipWhitespace()
    }

    const event: PatternEvent = {
      type: isChord ? 'chord' : 'note',
      values,
      duration: 1,
      velocity: 1,
      weight: 1,
      replicate: 1,
      probability: 1,
      multiplier: 1,
      divisor: 1,
      glide: 0,
      hold: 0,
      line: startLine,
      column: startColumn,
      length: 0, // Will be set after modifiers
    }

    const modifiedEvent = this.applyModifiers(event)
    // Calculate length excluding trailing whitespace and newlines
    const endPos = this.pos
    // Backtrack to find the actual end of the event (excluding trailing whitespace and newlines)
    let actualEndPos = endPos
    while (actualEndPos > startPos) {
      const char = this.input[actualEndPos - 1]
      if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
        actualEndPos--
      }
      else {
        break
      }
    }
    modifiedEvent.length = actualEndPos - startPos
    return modifiedEvent
  }

  private parseValue(): string | null {
    const char = this.peek()

    // Check for quoted string (single or double quotes)
    if (char === '\'' || char === '"') {
      const quote = char
      this.advancePosition() // Skip opening quote

      let value = ''
      let loopCount = 0
      const maxLoops = 10000 // Safety limit for multiline strings

      while (this.pos < this.input.length && loopCount < maxLoops) {
        loopCount++
        const current = this.peek()

        // Check for escape sequence
        if (current === '\\' && this.pos + 1 < this.input.length) {
          const next = this.input[this.pos + 1]
          if (next === quote || next === '\\') {
            value += next
            this.advancePosition() // Skip backslash
            this.advancePosition() // Skip escaped char
            continue
          }
        }

        // Check for closing quote
        if (current === quote) {
          this.advancePosition() // Skip closing quote
          return value
        }

        // Add character (including newlines for multiline support)
        value += current
        this.advancePosition()
      }

      // Unterminated string
      return value
    }

    // Parse note name or value (including Euclidean rhythm notation)
    let value = ''
    let loopCount = 0
    const maxLoops = 100 // Safety limit

    while (this.pos < this.input.length && loopCount < maxLoops) {
      loopCount++
      const currentChar = this.peek()

      // Check for Euclidean rhythm
      if (currentChar === '(' && value.length > 0) {
        const euclidean = this.parseEuclidean()
        if (euclidean) {
          return `${value}(${euclidean.beats},${euclidean.segments},${euclidean.offset})`
        }
      }

      // Stop at modifiers or separators
      if (
        currentChar === ' '
        || currentChar === '\t'
        || currentChar === '\n'
        || currentChar === '\r'
        || currentChar === ','
        || currentChar === '|'
        || currentChar === '['
        || currentChar === ']'
        || currentChar === '<'
        || currentChar === '>'
        || currentChar === ')'
        || currentChar === '*'
        || currentChar === '/'
        || currentChar === '.'
        || currentChar === '@'
        || currentChar === '!'
        || currentChar === '?'
        || currentChar === ';'
        || currentChar === '~'
        || currentChar === '_'
        || currentChar === '\\'
        || !currentChar
      ) {
        break
      }

      value += currentChar
      this.advancePosition()
    }

    return value || null
  }

  private parseEuclidean(): EuclideanRhythm | null {
    if (this.peek() !== '(') return null

    this.advancePosition() // Skip '('
    this.skipWhitespace()

    const beats = this.parseNumber()
    this.skipWhitespace()

    if (this.peek() !== ',') return null
    this.advancePosition()
    this.skipWhitespace()

    const segments = this.parseNumber()
    this.skipWhitespace()

    let offset = 0
    if (this.peek() === ',') {
      this.advancePosition()
      this.skipWhitespace()
      offset = this.parseNumber()
      this.skipWhitespace()
    }

    if (this.peek() === ')') {
      this.advancePosition()
    }

    return { beats, segments, offset }
  }

  private applyModifiersNoSkipWhitespace(event: PatternEvent): PatternEvent {
    // Like applyModifiers but doesn't skip leading whitespace
    // This is for nested brackets where whitespace indicates a new event

    // If there's whitespace, don't apply any modifiers - this indicates end of event
    const char = this.peek()
    if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
      return event
    }

    // Check for . (velocity) - must be followed by a digit
    // Format: .3 means 0.3, .5 means 0.5, etc.
    // Skip velocity modifiers for rests - they should remain as separate events
    if (event.type !== 'rest' && this.peek() === '.' && this.pos + 1 < this.input.length) {
      const nextChar = this.input[this.pos + 1]
      if (nextChar >= '0' && nextChar <= '9') {
        this.advancePosition() // Skip the dot
        const fraction = this.parseNumber()
        // Convert .3 to 0.3, .5 to 0.5, etc.
        event.velocity = fraction < 1 ? fraction : fraction / Math.pow(10, Math.floor(Math.log10(fraction)) + 1)
        this.skipWhitespace()
      }
    }

    // Check for \ (glide) - optional exponent after
    // Format: \ means glide with exponent 1.0, \.5 means glide with exponent 0.5
    // Skip glide for rests
    if (event.type !== 'rest' && this.peek() === '\\') {
      this.advancePosition() // Skip the backslash
      // Check if followed by a number
      if (this.peek() === '.' || (this.peek() >= '0' && this.peek() <= '9')) {
        const exponent = this.parseNumber()
        event.glide = exponent
      }
      else {
        // No number after backslash, use default exponent of 1.0
        event.glide = 1.0
      }
      this.skipWhitespace()
    }

    // Check for * (multiplication) - element level
    if (this.peek() === '*') {
      this.advancePosition()
      event.multiplier = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for / (division) - element level
    if (this.peek() === '/') {
      this.advancePosition()
      event.divisor = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for @ (elongation/weight)
    if (this.peek() === '@') {
      this.advancePosition()
      event.weight = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for ! (replication)
    if (this.peek() === '!') {
      this.advancePosition()
      event.replicate = this.parseNumber()
      console.log(
        `[MINI-NOTATION] Applied replication !${event.replicate} to event: [${
          event.values.join(',')
        }] (applyModifiersNoSkipWhitespace)`,
      )
      this.skipWhitespace()
    }

    // Check for ? (probability)
    if (this.peek() === '?') {
      this.advancePosition()
      const prob = this.parseNumber()
      event.probability = prob > 0 ? prob : 0.5 // Default 50% if no number
      this.skipWhitespace()
    }

    // Check for ; (hold/sustain) - must be followed by a number
    // Format: ;.3 means hold for 0.3 seconds, ;1 means hold for 1 second
    // Skip hold for rests
    if (event.type !== 'rest' && this.peek() === ';') {
      this.advancePosition() // Skip the semicolon
      // Check if followed by a number
      if (this.peek() === '.' || (this.peek() >= '0' && this.peek() <= '9')) {
        event.hold = this.parseNumber()
      }
      this.skipWhitespace()
    }

    return event
  }

  private applyModifiers(event: PatternEvent): PatternEvent {
    this.skipWhitespace()

    // Check for . (velocity) - must be followed by a digit
    // Format: .3 means 0.3, .5 means 0.5, etc.
    // Skip velocity modifiers for rests - they should remain as separate events
    if (event.type !== 'rest' && this.peek() === '.' && this.pos + 1 < this.input.length) {
      const nextChar = this.input[this.pos + 1]
      if (nextChar >= '0' && nextChar <= '9') {
        this.advancePosition() // Skip the dot
        const fraction = this.parseNumber()
        // Convert .3 to 0.3, .5 to 0.5, etc.
        event.velocity = fraction < 1 ? fraction : fraction / Math.pow(10, Math.floor(Math.log10(fraction)) + 1)
        this.skipWhitespace()
      }
    }

    // Check for \ (glide) - optional exponent after
    // Format: \ means glide with exponent 1.0, \.5 means glide with exponent 0.5
    // Skip glide for rests
    if (event.type !== 'rest' && this.peek() === '\\') {
      this.advancePosition() // Skip the backslash
      // Check if followed by a number
      if (this.peek() === '.' || (this.peek() >= '0' && this.peek() <= '9')) {
        const exponent = this.parseNumber()
        event.glide = exponent
      }
      else {
        // No number after backslash, use default exponent of 1.0
        event.glide = 1.0
      }
      this.skipWhitespace()
    }

    // Check for * (multiplication) - element level
    if (this.peek() === '*') {
      this.advancePosition()
      event.multiplier = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for / (division) - element level
    if (this.peek() === '/') {
      this.advancePosition()
      event.divisor = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for @ (elongation/weight)
    if (this.peek() === '@') {
      this.advancePosition()
      event.weight = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for ! (replication)
    if (this.peek() === '!') {
      this.advancePosition()
      event.replicate = this.parseNumber()
      this.skipWhitespace()
    }

    // Check for ? (probability)
    if (this.peek() === '?') {
      this.advancePosition()
      const prob = this.parseNumber()
      event.probability = prob > 0 ? prob : 0.5 // Default 50% if no number
      this.skipWhitespace()
    }

    // Check for ; (hold/sustain) - must be followed by a number
    // Format: ;.3 means hold for 0.3 seconds, ;1 means hold for 1 second
    // Skip hold for rests
    if (event.type !== 'rest' && this.peek() === ';') {
      this.advancePosition() // Skip the semicolon
      // Check if followed by a number
      if (this.peek() === '.' || (this.peek() >= '0' && this.peek() <= '9')) {
        event.hold = this.parseNumber()
      }
      this.skipWhitespace()
    }

    return event
  }

  private parseNumber(): number {
    let numStr = ''

    let loopCount = 0
    const maxLoops = 20 // Safety limit for number parsing

    while (this.pos < this.input.length && loopCount < maxLoops) {
      loopCount++
      const char = this.peek()
      if ((char >= '0' && char <= '9') || char === '.') {
        numStr += char
        this.advancePosition()
      }
      else {
        break
      }
    }

    const num = parseFloat(numStr)
    return isNaN(num) ? 1 : num
  }

  private peek(): string {
    return this.input[this.pos] || ''
  }

  private peekAhead(offset: number): string {
    return this.input[this.pos + offset] || ''
  }

  private advancePosition(): void {
    if (this.pos >= this.input.length) return

    const char = this.input[this.pos]
    if (char === '\n') {
      this.line++
      this.column = 1
      this.pos++
    }
    else if (char === '\r') {
      // Handle \r\n or standalone \r
      if (this.pos + 1 < this.input.length && this.input[this.pos + 1] === '\n') {
        this.pos++ // Skip \r
        this.line++
        this.column = 1
        this.pos++ // Skip \n
      }
      else {
        this.line++
        this.column = 1
        this.pos++
      }
    }
    else {
      this.column++
      this.pos++
    }
  }

  private skipWhitespace(): void {
    let loopCount = 0
    const maxLoops = 1000 // Safety limit

    while (this.pos < this.input.length && loopCount < maxLoops) {
      const char = this.input[this.pos]
      // Skip all whitespace: space, tab, newline, carriage return
      if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
        this.advancePosition()
        loopCount++
      }
      else {
        break
      }
    }
  }
}

/**
 * Seeded random number generator for reproducible randomness
 * Uses xorshift128+ algorithm for better distribution
 */
class SeededRandom {
  private state0: number
  private state1: number

  constructor(seed: number) {
    // Initialize state with seed mixing for better distribution
    // Use splitmix32 to initialize the states
    this.state0 = this.splitmix32(seed)
    this.state1 = this.splitmix32(this.state0)
  }

  private splitmix32(seed: number): number {
    let z = (seed + 0x9e3779b9) >>> 0
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0
    return (z ^ (z >>> 16)) >>> 0
  }

  random(): number {
    // xorshift128+ algorithm
    let s1 = this.state0
    const s0 = this.state1
    this.state0 = s0
    s1 ^= s1 << 23
    s1 ^= s1 >>> 17
    s1 ^= s0
    s1 ^= s0 >>> 26
    this.state1 = s1
    return ((this.state0 + this.state1) >>> 0) / 4294967296
  }
}

/**
 * Expand a pattern sequence into a flat list of timed events
 * Returns an array of [time, values, duration, velocity] tuples
 *
 * Note: Random choice (|) and probability (?) are evaluated at compile-time.
 * Each compilation will produce a different deterministic result.
 *
 * @param pattern The mini-notation pattern string
 * @param seed Optional seed for reproducible randomness (default: Math.random())
 * @param lineOffset Optional line offset to adjust positions (default: 0)
 * @param columnOffset Optional column offset to adjust positions (default: 0)
 * @param originalPattern Optional original pattern string before trimming (for position calculation)
 */
export function parsePattern(
  pattern: string,
  lineOffset: number = 0,
  columnOffset: number = 0,
  originalPattern?: string,
): PatternSequence {
  const parser = new PatternParser()
  return parser.parse(pattern, lineOffset, columnOffset, originalPattern)
}

export function expandPattern(
  pattern: string,
  seed?: number,
  lineOffset: number = 0,
  columnOffset: number = 0,
  originalPattern?: string,
): Array<[number, string[], number, number, number, number]> {
  const parser = new PatternParser()
  const seq = parser.parse(pattern, lineOffset, columnOffset, originalPattern)

  // Use seeded random if provided
  const rng = seed !== undefined ? new SeededRandom(seed) : null

  let events: Array<[number, string[], number, number, number, number]> = []

  function expandSequence(
    seq: PatternSequence,
    cycleStart: number,
    cycleDuration: number,
  ): void {
    // Handle alternating pattern type first
    if (seq.type === 'alternating') {
      // For top-level alternating patterns (cycleDuration = 1.0), treat as sequential for weights
      // For nested alternating patterns, each event gets its own cycle/bar
      const isTopLevel = cycleStart === 0 && cycleDuration === 1.0

      if (isTopLevel) {
        // For alternating patterns, each event maintains a consistent base duration
        // The cycle extends to accommodate all events, rather than compressing events to fit a fixed cycle
        // Base duration per event (default 0.25, so 4 events = 1.0 cycle)
        const baseEventDuration = 0.25

        // Calculate total cycle duration based on sum of all event durations
        // Weight and replication both extend the duration
        const totalWeight = seq.events.reduce((sum, e) => sum + e.weight * e.replicate, 0) || 1
        const totalCycleDuration = totalWeight * baseEventDuration * seq.divisor / seq.multiplier

        // Calculate event durations - each event gets baseEventDuration * weight * replicate
        // Apply sequence-level divisor (slows down) and multiplier (speeds up)
        const eventDurations = seq.events.map(e => baseEventDuration * e.weight * e.replicate * seq.divisor / seq.multiplier)

        // For nested alternating patterns in top-level alternating patterns, we need to expand
        // enough cycles to show the alternation. However, to avoid UI confusion with duplicate
        // events, we'll expand to show alternation but the UI should handle this specially.
        // For nested alternating patterns, we expand to show alternation.
        // The sequencer will loop through all expanded events, so we need to structure
        // the expansion so that when it loops, it alternates correctly.
        //
        // For <a2 a3 <a4 a5>>, we want:
        //   Cycle 1: a2, a3, a4
        //   Cycle 2: a2, a3, a5
        //   Cycle 3: a2, a3, a4
        //   etc.
        //
        // If we expand to 6 events: a2, a3, a4, a2, a3, a5
        // The sequencer loops every 6 events, so it plays the same sequence each loop.
        // This doesn't alternate!
        //
        // The solution: expand to a single cycle only (3 events: a2, a3, a4).
        // The sequencer will loop every 3 events, so it will play: a2, a3, a4, a2, a3, a4...
        // But we want it to alternate to a5 on the second loop.
        //
        // Since the sequencer doesn't track loop iterations, we can't make it alternate.
        // We need to expand to show the alternation, but structure it so the sequencer
        // loops at the right boundary. Actually, we can't do that with the current sequencer.
        //
        // Let's try expanding to multiple cycles anyway, and see if we can make it work
        // by ensuring the pattern structure is correct.
        let effectiveMultiplier = seq.multiplier
        for (const event of seq.events) {
          if ((event as any).nested && (event as any).nested.type === 'alternating') {
            const nestedLength = (event as any).nested.events.length
            // Expand to show full alternation
            effectiveMultiplier = Math.max(effectiveMultiplier, nestedLength)
          }
        }

        // Repeat the pattern based on effective multiplier
        // Each repetition uses the full cycle duration
        for (let rep = 0; rep < effectiveMultiplier; rep++) {
          const repStart = cycleStart + (rep * totalCycleDuration)
          let time = repStart

          for (let eventIndex = 0; eventIndex < seq.events.length; eventIndex++) {
            const event = seq.events[eventIndex]
            // Use pre-calculated duration based on full cycle (don't divide by multiplier)
            let eventDuration = eventDurations[eventIndex]

            // Apply element-level division (makes it slower/longer)
            eventDuration = eventDuration * event.divisor

            if (event.type === 'rest') {
              events.push([time, [], eventDuration, 0, 0, 0])
              time += eventDuration
              continue
            }

            // Apply probability at compile-time
            const randomValue = rng ? rng.random() : Math.random()
            const shouldPlay = randomValue < event.probability

            if (shouldPlay) {
              // Handle nested sequences
              if ((event as any).nested) {
                const nested = (event as any).nested
                const adjustedDuration = (nested.divisor > 1 && eventDuration < 1.0)
                  ? eventDuration / nested.divisor
                  : eventDuration

                // If nested is an alternating pattern, cycle through its events sequentially
                // For nested alternating patterns in top-level alternating patterns,
                // we need to track which cycle iteration we're in.
                // Since the sequencer will loop the pattern, we calculate based on:
                // - How many times the outer pattern has repeated (rep)
                // - Which event position this is in the outer pattern (eventIndex)
                // This gives us a unique counter for each time we encounter this nested pattern
                if (nested.type === 'alternating' && nested.events.length > 0) {
                  // Calculate a cycle counter: each time we go through the outer pattern,
                  // we increment. For a pattern with N events, rep*N + eventIndex gives us
                  // a unique number for each event occurrence across all repetitions.
                  // But we only care about when we hit THIS nested pattern, so we use
                  // just the repetition count since this nested pattern appears at a fixed position.
                  const nestedEventIndex = rep % nested.events.length
                  const nestedEvent = nested.events[nestedEventIndex]

                  // For nested alternating patterns in top-level alternating patterns,
                  // the selected event gets the full allocated duration
                  let nestedEventDuration = adjustedDuration
                  // Apply element-level division (makes it slower/longer)
                  nestedEventDuration = nestedEventDuration * nestedEvent.divisor

                  // Handle nested event (could be another nested sequence, rest, or regular event)
                  if (nestedEvent.type === 'rest') {
                    events.push([time, [], nestedEventDuration, 0, 0, 0])
                  }
                  else if ((nestedEvent as any).nested) {
                    // Recursively expand nested-nested sequence
                    expandSequence((nestedEvent as any).nested, time, nestedEventDuration)
                  }
                  else {
                    // Regular event - apply replication and multiplication
                    const repeatCount = nestedEvent.multiplier * nestedEvent.replicate
                    const repeatDuration = nestedEventDuration / repeatCount

                    for (let r = 0; r < repeatCount; r++) {
                      const eventTime = time + r * repeatDuration
                      let valuesToUse = nestedEvent.values
                      if ((nestedEvent as any).randomChoice && nestedEvent.values.length > 0) {
                        const randomValue = rng ? rng.random() : Math.random()
                        const chosenIndex = Math.floor(randomValue * nestedEvent.values.length)
                        valuesToUse = [nestedEvent.values[chosenIndex]]
                      }
                      events.push([eventTime, valuesToUse, repeatDuration, nestedEvent.velocity, nestedEvent.glide, nestedEvent.hold])
                    }
                  }
                }
                else {
                  // Non-alternating nested pattern - expand normally
                  expandSequence(nested, time, adjustedDuration)
                }
              }
              else {
                // For top-level alternating patterns, replication extends duration (already in eventDuration)
                // But we still need to repeat the event the specified number of times
                // Apply element-level multiplication and replication
                const repeatCount = event.multiplier * event.replicate
                const repeatDuration = eventDuration / repeatCount

                for (let r = 0; r < repeatCount; r++) {
                  const eventTime = time + r * repeatDuration

                  // Handle random choice chords
                  let valuesToUse = event.values
                  if ((event as any).randomChoice && event.values.length > 0) {
                    const randomValue = rng ? rng.random() : Math.random()
                    const chosenIndex = Math.floor(randomValue * event.values.length)
                    valuesToUse = [event.values[chosenIndex]]
                  }
                  events.push([eventTime, valuesToUse, repeatDuration, event.velocity, event.glide, event.hold])
                }
              }
            }
            else {
              // Probability failed - insert rest
              events.push([time, [], eventDuration, -1, 0, 0])
            }

            time += eventDuration
          }
        }
        return
      }

      // For nested alternating patterns, each event gets its own cycle/bar
      // So <a b c> plays 'a' in bar 0, 'b' in bar 1, 'c' in bar 2, then loops
      const nestedCycleDuration = seq.divisor / seq.multiplier

      if (seq.events.length > 0) {
        // Calculate total weight for proportional distribution
        const totalWeight = seq.events.reduce((sum, e) => sum + e.weight, 0) || 1

        // Generate one bar per event
        for (let eventIndex = 0; eventIndex < seq.events.length; eventIndex++) {
          const event = seq.events[eventIndex]
          const nestedCycleStart = cycleStart + (eventIndex * nestedCycleDuration)

          let time = nestedCycleStart

          // For alternating patterns, weight extends the event duration proportionally
          // Event with weight 2 gets 2x the duration relative to weight 1 events
          let eventDuration = (nestedCycleDuration / totalWeight) * event.weight

          // Apply element-level division (makes it slower/longer)
          eventDuration = eventDuration * event.divisor

          if (event.type === 'rest') {
            // Produce a rest event (empty values, velocity 0, no glide)
            events.push([time, [], eventDuration, 0, 0, 0])
            continue
          }

          // Handle random choice chords - pick one value randomly first
          let valuesToProcess = event.values
          if ((event as any).randomChoice && event.values.length > 0) {
            const randomValue = rng ? rng.random() : Math.random()
            const chosenIndex = Math.floor(randomValue * event.values.length)
            valuesToProcess = [event.values[chosenIndex]]
          }

          // Check if any value has Euclidean notation
          const hasEuclidean = valuesToProcess.some(v => /\(\d+,\d+(?:,\d+)?\)/.test(v))

          if (hasEuclidean) {
            // Expand Euclidean rhythms
            for (const value of valuesToProcess) {
              const euclideanMatch = value.match(/^(.+?)\((\d+),(\d+)(?:,(\d+))?\)$/)
              if (euclideanMatch) {
                const noteName = euclideanMatch[1]
                const beats = parseInt(euclideanMatch[2])
                const segments = parseInt(euclideanMatch[3])
                const offset = euclideanMatch[4] ? parseInt(euclideanMatch[4]) : 0

                const pattern = euclidean(beats, segments, offset)
                const stepDuration = eventDuration / segments

                // Find all beat positions
                const beatPositions: number[] = []
                for (let i = 0; i < segments; i++) {
                  if (pattern[i]) {
                    beatPositions.push(i)
                  }
                }

                // Create events with durations lasting until next beat
                for (let b = 0; b < beatPositions.length; b++) {
                  const beatIndex = beatPositions[b]
                  const startTime = time + beatIndex * stepDuration

                  // Duration lasts until next beat or end of pattern
                  let duration: number
                  if (b < beatPositions.length - 1) {
                    const nextBeatIndex = beatPositions[b + 1]
                    duration = (nextBeatIndex - beatIndex) * stepDuration
                  }
                  else {
                    // Last beat - duration until end of event
                    duration = (segments - beatIndex) * stepDuration
                  }

                  events.push([startTime, [noteName], duration, event.velocity, event.glide, event.hold])
                }
              }
              else {
                // Regular value, treat as normal event
                events.push([time, [value], eventDuration, event.velocity, event.glide, event.hold])
              }
            }
            continue
          }

          // Apply probability at compile-time
          const randomValue = rng ? rng.random() : Math.random()
          const shouldPlay = randomValue < event.probability // Fire if random < probability

          if (shouldPlay) {
            // Handle nested sequences
            // Adjust cycle duration for divisor so nested sequence fits within allocated eventDuration
            // Only apply this adjustment when eventDuration < 1.0 (indicating allocated from parent)
            // For top-level patterns (eventDuration = 1.0), let divisor extend duration naturally
            if ((event as any).nested) {
              const nested = (event as any).nested
              const adjustedDuration = (nested.divisor > 1 && eventDuration < 1.0)
                ? eventDuration / nested.divisor
                : eventDuration
              expandSequence(nested, time, adjustedDuration)
            }
            else {
              // Apply element-level multiplication and replication
              const repeatCount = event.multiplier * event.replicate
              const repeatDuration = eventDuration / repeatCount

              for (let r = 0; r < repeatCount; r++) {
                const eventTime = time + r * repeatDuration

                // Handle random choice chords - pick one value randomly
                let valuesToUse = event.values
                if ((event as any).randomChoice && event.values.length > 0) {
                  const randomValue = rng ? rng.random() : Math.random()
                  const chosenIndex = Math.floor(randomValue * event.values.length)
                  valuesToUse = [event.values[chosenIndex]]
                }
                events.push([eventTime, valuesToUse, repeatDuration, event.velocity, event.glide, event.hold])
              }
            }
          }
          else {
            // Probability failed - insert rest (empty values with velocity -1, no glide)
            events.push([time, [], eventDuration, -1, 0, 0])
          }
        }
      }
      return
    }

    // Sequential pattern handling
    // Calculate effective duration considering division and multiplication
    // For nested sequences: ensure total duration equals cycleDuration
    // Multiplier repeats the pattern, divisor slows it down
    // We want: effectiveCycleDuration * multiplier = cycleDuration / divisor
    // So: effectiveCycleDuration = cycleDuration / (divisor * multiplier)
    // But this makes divisor compress instead of extend, so we need to apply divisor to event durations instead
    // Actually, let's keep the original formula but ensure it works for nested sequences
    const effectiveCycleDuration = cycleDuration * seq.divisor / seq.multiplier

    // Calculate total weight of all events
    // Nested patterns count as 1 event regardless of their internal structure
    const totalWeight = seq.events.reduce((sum, e) => sum + e.weight, 0) || 1

    // Repeat the pattern based on multiplier
    for (let rep = 0; rep < seq.multiplier; rep++) {
      const repStart = cycleStart + (rep * effectiveCycleDuration)
      let time = repStart

      for (const event of seq.events) {
        // Calculate base duration for this event
        let eventDuration = (effectiveCycleDuration / totalWeight) * event.weight

        // Apply element-level division (makes it slower/longer)
        eventDuration = eventDuration * event.divisor

        if (event.type === 'rest') {
          // Produce a rest event (empty values, velocity 0, no glide)
          events.push([time, [], eventDuration, 0, 0, 0])
          time += eventDuration
          continue
        }

        // Handle random choice chords - pick one value randomly first
        let valuesToProcess = event.values
        if ((event as any).randomChoice && event.values.length > 0) {
          const randomValue = rng ? rng.random() : Math.random()
          const chosenIndex = Math.floor(randomValue * event.values.length)
          valuesToProcess = [event.values[chosenIndex]]
        }

        // Check if any value has Euclidean notation
        const hasEuclidean = valuesToProcess.some(v => /\(\d+,\d+(?:,\d+)?\)/.test(v))

        if (hasEuclidean) {
          // Expand Euclidean rhythms
          for (const value of valuesToProcess) {
            const euclideanMatch = value.match(/^(.+?)\((\d+),(\d+)(?:,(\d+))?\)$/)
            if (euclideanMatch) {
              const noteName = euclideanMatch[1]
              const beats = parseInt(euclideanMatch[2])
              const segments = parseInt(euclideanMatch[3])
              const offset = euclideanMatch[4] ? parseInt(euclideanMatch[4]) : 0

              const pattern = euclidean(beats, segments, offset)
              const stepDuration = eventDuration / segments

              // Find all beat positions
              const beatPositions: number[] = []
              for (let i = 0; i < segments; i++) {
                if (pattern[i]) {
                  beatPositions.push(i)
                }
              }

              // Create events with durations lasting until next beat
              for (let b = 0; b < beatPositions.length; b++) {
                const beatIndex = beatPositions[b]
                const startTime = time + beatIndex * stepDuration

                // Duration lasts until next beat or end of pattern
                let duration: number
                if (b < beatPositions.length - 1) {
                  const nextBeatIndex = beatPositions[b + 1]
                  duration = (nextBeatIndex - beatIndex) * stepDuration
                }
                else {
                  // Last beat - duration until end of event
                  duration = (segments - beatIndex) * stepDuration
                }

                events.push([startTime, [noteName], duration, event.velocity, event.glide, event.hold])
              }
            }
            else {
              // Regular value, treat as normal event
              events.push([time, [value], eventDuration, event.velocity, event.glide, event.hold])
            }
          }
          time += eventDuration
          continue
        }

        // Apply probability at compile-time
        const randomValue = rng ? rng.random() : Math.random()
        const shouldPlay = randomValue < event.probability // Fire if random < probability

        if (shouldPlay) {
          // Handle nested sequences
          // Adjust cycle duration for divisor so nested sequence fits within allocated eventDuration
          // Only apply this adjustment when eventDuration < 1.0 (indicating allocated from parent)
          // For top-level patterns (eventDuration = 1.0), let divisor extend duration naturally
          if ((event as any).nested) {
            const nested = (event as any).nested
            const adjustedDuration = (nested.divisor > 1 && eventDuration < 1.0)
              ? eventDuration / nested.divisor
              : eventDuration
            expandSequence(nested, time, adjustedDuration)
          }
          else {
            // Apply element-level multiplication and replication
            const repeatCount = event.multiplier * event.replicate
            const repeatDuration = eventDuration / repeatCount

            for (let r = 0; r < repeatCount; r++) {
              const eventTime = time + r * repeatDuration

              // Handle random choice chords - pick one value randomly
              let valuesToUse = event.values
              if ((event as any).randomChoice && event.values.length > 0) {
                const randomValue = rng ? rng.random() : Math.random()
                const chosenIndex = Math.floor(randomValue * event.values.length)
                valuesToUse = [event.values[chosenIndex]]
              }
              events.push([eventTime, valuesToUse, repeatDuration, event.velocity, event.glide, event.hold])
            }
          }
        }
        else {
          // Probability failed - insert rest (empty values with velocity -1, no glide)
          events.push([time, [], eventDuration, -1, 0, 0])
        }

        time += eventDuration
      }
    }
  }

  // Handle different sequence types at top level
  if (seq.type === 'alternating') {
    // For top-level alternating patterns, call expandSequence which handles it
    expandSequence(seq, 0, 1)
  }
  else if (seq.type === 'random') {
    // Pick random event at compile-time
    const totalDuration = seq.divisor / seq.multiplier
    const randomValue = rng ? rng.random() : Math.random()
    const event = seq.events[Math.floor(randomValue * seq.events.length)]

    if (event && event.type !== 'rest') {
      // Handle random choice chords - pick one value randomly first
      let valuesToProcess = event.values
      if ((event as any).randomChoice && event.values.length > 0) {
        const randomValue = rng ? rng.random() : Math.random()
        const chosenIndex = Math.floor(randomValue * event.values.length)
        valuesToProcess = [event.values[chosenIndex]]
      }

      // Check if any value has Euclidean notation
      const hasEuclidean = valuesToProcess.some(v => /\(\d+,\d+(?:,\d+)?\)/.test(v))

      if (hasEuclidean) {
        // Expand Euclidean rhythms
        for (const value of valuesToProcess) {
          const euclideanMatch = value.match(/^(.+?)\((\d+),(\d+)(?:,(\d+))?\)$/)
          if (euclideanMatch) {
            const noteName = euclideanMatch[1]
            const beats = parseInt(euclideanMatch[2])
            const segments = parseInt(euclideanMatch[3])
            const offset = euclideanMatch[4] ? parseInt(euclideanMatch[4]) : 0

            const pattern = euclidean(beats, segments, offset)
            const stepDuration = totalDuration / segments

            // Find all beat positions
            const beatPositions: number[] = []
            for (let i = 0; i < segments; i++) {
              if (pattern[i]) {
                beatPositions.push(i)
              }
            }

            // Create events with durations lasting until next beat
            for (let b = 0; b < beatPositions.length; b++) {
              const beatIndex = beatPositions[b]
              const startTime = beatIndex * stepDuration

              // Duration lasts until next beat or end of pattern
              let duration: number
              if (b < beatPositions.length - 1) {
                const nextBeatIndex = beatPositions[b + 1]
                duration = (nextBeatIndex - beatIndex) * stepDuration
              }
              else {
                // Last beat - duration until end of event
                duration = (segments - beatIndex) * stepDuration
              }

              events.push([startTime, [noteName], duration, event.velocity, event.glide, event.hold])
            }
          }
          else {
            // Regular value, treat as normal event
            events.push([0, [value], totalDuration, event.velocity, event.glide, event.hold])
          }
        }
      }
      else if ((event as any).nested) {
        // Handle nested sequences
        expandSequence((event as any).nested, 0, totalDuration)
      }
      else {
        // Handle random choice chords - pick one value randomly
        let valuesToUse = event.values
        if ((event as any).randomChoice && event.values.length > 0) {
          const randomValue = rng ? rng.random() : Math.random()
          const chosenIndex = Math.floor(randomValue * event.values.length)
          valuesToUse = [event.values[chosenIndex]]
        }
        events.push([0, valuesToUse, totalDuration, event.velocity, event.glide, event.hold])
      }
    }
  }
  else {
    // Sequential
    expandSequence(seq, 0, 1)
  }

  return events
}

/**
 * Expand a pattern with multiple seeded variations
 * Creates N deterministic variations of a pattern and joins them into one sequence
 *
 * @param pattern The mini-notation pattern string
 * @param variations Number of variations to generate (default: 1)
 * @param seed Base seed for variations (default: random)
 * @param lineOffset Optional line offset to adjust positions (default: 0)
 * @param columnOffset Optional column offset to adjust positions (default: 0)
 * @param originalPattern Optional original pattern string before trimming (for position calculation)
 * @param maxNotes Maximum allowed notes in final sequence (default: 128)
 * @returns Array of [time, values, duration, velocity] tuples
 * @throws Error if resulting sequence exceeds maxNotes
 */
export function expandPatternWithVariations(
  pattern: string,
  variations: number = 1,
  seed?: number,
  lineOffset: number = 0,
  columnOffset: number = 0,
  originalPattern?: string,
  maxNotes: number = 128,
): Array<[number, string[], number, number, number, number]> {
  // Validate variations count
  if (variations < 1) {
    throw new Error(`variations must be >= 1, got ${variations}`)
  }
  if (variations > 1000) {
    throw new Error(`variations cannot exceed 1000 (got ${variations}) to prevent excessive memory usage`)
  }

  // Generate base seed if not provided
  const baseSeed = seed !== undefined ? seed : Math.floor(Math.random() * 4294967296)

  // Expand first variation to get cycle length
  const firstVariation = expandPattern(pattern, baseSeed, lineOffset, columnOffset, originalPattern)
  if (firstVariation.length === 0) {
    return []
  }

  // Calculate total cycle duration
  const cycleDuration = firstVariation.reduce((max, [time, _, duration]) => Math.max(max, time + duration), 0)

  // Check if we'll exceed max notes
  const estimatedTotalNotes = firstVariation.length * variations
  if (estimatedTotalNotes > maxNotes) {
    throw new Error(
      `Pattern with ${variations} variations would generate ~${estimatedTotalNotes} notes, `
        + `which exceeds the maximum of ${maxNotes}. `
        + `Try reducing variations (max ~${Math.floor(maxNotes / firstVariation.length)}) or simplifying the pattern.`,
    )
  }

  // Generate all variations
  const allEvents: Array<[number, string[], number, number, number, number]> = []

  for (let v = 0; v < variations; v++) {
    // Use different seed for each variation
    const variationSeed = baseSeed + v
    const events = expandPattern(pattern, variationSeed, lineOffset, columnOffset, originalPattern)

    // Offset events by cycle time
    const offset = v * cycleDuration
    for (const [time, values, duration, velocity, glide, hold] of events) {
      allEvents.push([time + offset, values, duration, velocity, glide, hold])
    }
  }

  // Final check against actual count
  if (allEvents.length > maxNotes) {
    throw new Error(
      `Pattern generated ${allEvents.length} notes, which exceeds the maximum of ${maxNotes}. `
        + `Reduce variations or simplify the pattern.`,
    )
  }

  return allEvents
}

/**
 * Convert Mini-Notation pattern to simple sequence format (compatible with existing seq())
 * Returns string like "c e g b" with durations encoded
 */
export function miniNotationToSequence(pattern: string): string {
  const events = expandPattern(pattern)

  // Sort by time
  events.sort((a, b) => a[0] - b[0])

  // Convert to simple format
  const notes: string[] = []

  for (const [time, values, duration] of events) {
    if (values.length === 1) {
      notes.push(values[0])
    }
    else {
      // Chord notation
      notes.push(`[${values.join(',')}]`)
    }
  }

  return notes.join(' ')
}

/**
 * Expand Euclidean rhythm using Bresenham-like algorithm
 * E(k,n) distributes k beats as evenly as possible over n steps
 */
export function euclidean(beats: number, segments: number, offset: number = 0): boolean[] {
  if (segments === 0) {
    return []
  }

  if (beats === 0) {
    return Array(segments).fill(false)
  }

  // Use Bresenham-like algorithm to distribute hits evenly across steps
  // Start with threshold at segments to place first beat at position 0
  const pattern: boolean[] = []
  let accumulator = segments

  for (let i = 0; i < segments; i++) {
    if (accumulator >= segments) {
      pattern.push(true)
      accumulator -= segments
    }
    else {
      pattern.push(false)
    }
    accumulator += beats
  }

  // Apply offset by rotating
  if (offset !== 0) {
    const normalizedOffset = ((offset % segments) + segments) % segments
    return [...pattern.slice(normalizedOffset), ...pattern.slice(0, normalizedOffset)]
  }

  return pattern
}
